import IAddress from '../models/IAddress';
import IAssessment from '../models/IAssessment';
import { AddressComponentMapping } from '../models/IAssessmentElement';
import IOption from '../models/IOption';
import AddressLookupService from '../services/AddressLookupService';

import AssessmentHelpers from './AssessmentHelpers';
import AssessmentLocalStorageManager from './AssessmentLocalStorageManager';
import useQuestionManager from './useQuestionManager';

type AddressSearchResult = {
	options: IOption[];
	continuationToken?: string;
};

export default function useAddressLookupManager()
{
	const addressLookupService= new AddressLookupService();
	const questionManager = useQuestionManager();

	const  searchAddresses = async (searchQuery: string, continuationToken?: string): Promise<AddressSearchResult> =>
	{
		if (!searchQuery || searchQuery.length === 0)
			return { options: [], continuationToken: undefined };

		try
		{
			const result = await addressLookupService.searchAddresses(searchQuery, {
				pageSize: 10,
				continuationToken
			});

			// Convert addresses to IOption format for SmartSearch
			const options = result.items.map(address => ({
				text: address.displayAddress,
				value: address.id,
				data: address // Store full address object for later use
			} as IOption));

			return {
				options,
				continuationToken: result.continuationToken
			};
		}
		catch (error)
		{
			console.error('Failed to search addresses:', error);
			return { options: [], continuationToken: undefined };
		}
	}

	/**
     * Populates address fields in an assessment based on address component mappings.
     *
     * This method takes a selected address and maps its components to specific fields
     * in the assessment based on the provided mappings. It updates both local storage
     * and Redux state for the target fields.
     *
     * @param assessmentId - The unique identifier of the assessment
     * @param questionId - The identifier of the question triggering the address population
     * @param selectedAddress - The address object containing the address components to map
     * @param addressMappings - Array of mappings that define which address components
     *                         should be mapped to which target fields in the assessment
     *
     * @returns void
     *
     * @remarks
     * - The method requires an assessment to be present in the Redux store
     * - If a target field is not found in the assessment, a warning is logged
     * - Only non-null and non-undefined address values are populated
     * - Both AssessmentLocalStorageManager and Redux state are updated for consistency
     *
     * @example
     * ```typescript
     * const mappings = [
     *   { addressComponent: 'street', targetFieldId: 'field-123' },
     *   { addressComponent: 'city', targetFieldId: 'field-456' }
     * ];
     *
     * populateAddressFields(
     *   'assessment-1',
     *   'question-1',
     *   { street: '123 Main St', city: 'Springfield' },
     *   mappings
     * );
     * ```
     */
	const populateAddressFields = (
		assessmentId: string,
		selectedAddress: IAddress,
		addressMappings: AddressComponentMapping[],
		assessment: IAssessment | null
	): void =>
	{
		if (!assessment)
		{
			console.error('No assessment found in state');
			return;
		}

		// Get all assessment elements to find target fields
		const allElements = AssessmentHelpers.getAllAssessmentQuestions(assessment);

		// Iterate through mappings and populate target fields
		addressMappings.forEach(mapping =>
		{
			const targetElement = allElements.find(el => el.referenceId === mapping.targetFieldId);

			if (!targetElement)
			{
				console.warn(`Target field ${mapping.targetFieldId} not found in assessment`);
				return;
			}

			// Get the value from the address object
			const addressValue = selectedAddress[mapping.addressComponent];

			if (addressValue !== undefined && addressValue !== null)
			{
				// Store the value for the target field
				AssessmentLocalStorageManager.setAnswerValues(
					assessmentId,
					targetElement.id,
					[addressValue]
				);

				// Update Redux state for the target field
				questionManager.updateAnswerValue(targetElement.id, [addressValue]);
			}
		});
	}

	return {
		searchAddresses,
		populateAddressFields
	} as const;
}
